import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  DEFAULT_UI_STATE,
  UI_STATE_KEYS,
  type UiState,
  type UiActiveView,
  type UiSessionListTab,
  type UiSessionListSort,
  type UiPlansStatusFilter,
  type UiWorkItemKind,
  type UiWorkItemState,
} from '@voice-assistant/shared';
import { useDrawerStore, openDrawer } from './drawerStore';

// ════════════════════════════════════════════════════════════════════════════
// uiStateStore — Class-B restorable view state ("non-destructive reload").
//
// Phase 1 of plans/user-ui-preferences-state.md (#1566): the single owner of
// device-local "where I was" view state, persisted under `va-ui-state-v1` so an
// accidental reload / pull-to-refresh rehydrates the operator where they left
// off instead of dumping them to a fresh default view.
//
// Phase 2 (#1979) folds the high-value subsystems in: session-list sort +
// filters + scroll, tab filters (Plans status, Tasks repos/kind/state), and
// file-drawer sections + scroll + upload-target — migrating their ad-hoc
// storage (valko-wi-selected-repos, files-drawer-collapsed-*, file-drawer-sections,
// sessionStorage files-drawer-scroll) into this store (R-A4: no parallel keys).
//
// Applies the workspace Frontend FSM pattern
// (server-config/docs/frontend-fsm-pattern.md / docs/frontend-state-architecture.md):
//   1. Explicit FSM        — three tiny orthogonal machines (activeView,
//                            sessionListTab, fileDrawerOpen) + the exported
//                            TRANSITIONS table (R-A1).
//   2. Origin-keyed store  — the origin is the APP-GLOBAL UI SINGLETON (like
//                            drawerStore): one device, one restorable view state.
//                            persist middleware (never manual getItem/setItem),
//                            `partialize` whitelist = UI_STATE_KEYS, `migrate`
//                            drops unknown/invalid keys (rename-safe restore).
//   3. Server-authoritative — Phase 1 is deliberately LOCAL-ONLY (operator
//                            decision, plan §4): Class-B view state has no server
//                            truth until the Phase 4 cross-device resume subset.
//                            No reconcile() yet by design.
//   4. Atomic transitions  — one intent = one named action (setActiveView,
//                            setSessionListTab, setFileDrawerOpen). Consumers
//                            never call setState (R-A2).
//   5. Setpoint evals      — setpoint-evals/ui-state/ (SE-UI-01 reload restore +
//                            fsm coverage + no-parallel-keys) + uiStateStore.spec.ts.
//
// Ownership boundaries (R-A4 — no parallel keys):
//   • activeView      — MIGRATED from the ad-hoc `voice-assistant-active-view`
//                       localStorage key (saveActiveView/loadActiveView in
//                       useSocket.ts, PR #747 — now removed). One-time legacy
//                       seed below deletes the old key.
//   • sessionListTab  — MIGRATED from SessionListView's component-local
//                       `useState` (was Class-C-by-accident: lost on reload).
//   • fileDrawerOpen  — a persisted MIRROR of `drawerStore.panel === 'files'`.
//                       drawerStore remains the live FSM owner of the drawer
//                       surface (which panel is open NOW); this store owns the
//                       Class-B fact "the files drawer was open when the page
//                       unloaded". drawerStore's own persist entry (`va-drawer-v1`)
//                       still demotes phase to 'closed' on rehydrate for every
//                       panel; the FILES drawer alone is re-opened at boot via
//                       bootRestoreFileDrawer() (called from useChatShell's mount
//                       anchor effect, composing with the #s/<id> deep-link scheme).
// ════════════════════════════════════════════════════════════════════════════

const LEGACY_ACTIVE_VIEW_KEY = 'voice-assistant-active-view';
// Phase 2 (#1979) folded ad-hoc keys — read+deleted once, never written again.
const LEGACY_WI_REPOS_KEY = 'valko-wi-selected-repos';
const LEGACY_COLLAPSED_PROJECTS_KEY = 'files-drawer-collapsed-projects';
const LEGACY_COLLAPSED_SECTIONS_KEY = 'files-drawer-collapsed-sections';
const LEGACY_OPEN_SECTIONS_KEY = 'file-drawer-sections';
const LEGACY_DRAWER_SCROLL_KEY = 'files-drawer-scroll'; // lived in sessionStorage
const STORE_KEY = 'va-ui-state-v1';

// ─── Transition table (R-A1) ────────────────────────────────────────────────────
//
// Three orthogonal sub-machines, one row per directed transition. Each `trigger`
// is a NAMED store action (R-A2). setpoint-evals/ui-state/SE-UI-02-fsm-coverage
// asserts every distinct trigger below is covered by a `// SE-COVERS:` marker in
// the behavioral spec (uiStateStore.spec.ts) — adding a row MUST add coverage.

export const TRANSITIONS: Array<{ from: string; to: string; trigger: string }> = [
  { from: 'view:list', to: 'view:chat', trigger: 'setActiveView' },
  { from: 'view:chat', to: 'view:list', trigger: 'setActiveView' },
  { from: 'tab:current', to: 'tab:other', trigger: 'setSessionListTab' },
  { from: 'fileDrawer:closed', to: 'fileDrawer:open', trigger: 'setFileDrawerOpen' },
  { from: 'fileDrawer:open', to: 'fileDrawer:closed', trigger: 'setFileDrawerOpen' },
  // Phase 2 (#1979)
  { from: 'sort:current', to: 'sort:other', trigger: 'setSessionListSort' },
  { from: 'listFilters:current', to: 'listFilters:other', trigger: 'setSessionListFilters' },
  { from: 'issueFilter:set', to: 'issueFilter:cleared', trigger: 'setSessionListIssueFilter' },
  { from: 'listScroll:top', to: 'listScroll:scrolled', trigger: 'setSessionListScroll' },
  { from: 'plansFilter:current', to: 'plansFilter:other', trigger: 'setPlansStatusFilter' },
  { from: 'wiFilters:current', to: 'wiFilters:other', trigger: 'setWorkItemsFilters' },
  { from: 'wiRepos:current', to: 'wiRepos:other', trigger: 'setWorkItemsRepos' },
  { from: 'drawerProject:expanded', to: 'drawerProject:collapsed', trigger: 'toggleFileDrawerCollapsedProject' },
  { from: 'drawerSection:expanded', to: 'drawerSection:collapsed', trigger: 'toggleFileDrawerCollapsedSection' },
  { from: 'drawerSections:current', to: 'drawerSections:other', trigger: 'setFileDrawerOpenSections' },
  { from: 'drawerScroll:top', to: 'drawerScroll:scrolled', trigger: 'setFileDrawerScroll' },
  { from: 'uploadProject:current', to: 'uploadProject:other', trigger: 'setFileDrawerUploadProject' },
];

// ─── Validation helpers (migrate + legacy seed share these) ─────────────────────

function isActiveView(v: unknown): v is UiActiveView {
  return v === 'list' || v === 'chat';
}

function isSessionListTab(v: unknown): v is UiSessionListTab {
  return v === 'sessions' || v === 'plans' || v === 'roads';
}

// Phase 2 guards
function isSessionListSort(v: unknown): v is UiSessionListSort {
  return v === 'recent' || v === 'name';
}

function isPlansStatusFilter(v: unknown): v is UiPlansStatusFilter {
  return v === 'all' || v === 'active' || v === 'executing' || v === 'stale';
}

function isWorkItemKind(v: unknown): v is UiWorkItemKind {
  return v === 'both' || v === 'issue' || v === 'pr';
}

function isWorkItemState(v: unknown): v is UiWorkItemState {
  return v === 'open' || v === 'closed' || v === 'all';
}

function isNonNegNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0;
}

function isIssueFilter(v: unknown): v is number | null {
  return v === null || isNonNegNumber(v);
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

function isBoolRecord(v: unknown): v is Record<string, boolean> {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  return Object.values(v).every((x) => typeof x === 'boolean');
}

/**
 * Build a clean UiState from an untrusted persisted blob: copy ONLY whitelisted
 * keys (UI_STATE_KEYS) whose values pass their type guard; everything else falls
 * back to DEFAULT_UI_STATE. Unknown keys are DROPPED — a renamed/removed key can
 * never poison a future rehydrate (plan §3 principle 5).
 */
export function sanitizeUiState(persisted: unknown): UiState {
  const src = (persisted ?? {}) as Record<string, unknown>;
  const clean: UiState = { ...DEFAULT_UI_STATE };
  for (const key of UI_STATE_KEYS) {
    const v = src[key];
    switch (key) {
      case 'activeView': if (isActiveView(v)) clean.activeView = v; break;
      case 'sessionListTab': if (isSessionListTab(v)) clean.sessionListTab = v; break;
      case 'fileDrawerOpen': if (typeof v === 'boolean') clean.fileDrawerOpen = v; break;
      case 'sessionListSort': if (isSessionListSort(v)) clean.sessionListSort = v; break;
      case 'sessionListShowInProgress': if (typeof v === 'boolean') clean.sessionListShowInProgress = v; break;
      case 'sessionListShowAutonomous': if (typeof v === 'boolean') clean.sessionListShowAutonomous = v; break;
      case 'sessionListIssueFilter': if (isIssueFilter(v)) clean.sessionListIssueFilter = v; break;
      case 'sessionListScroll': if (isNonNegNumber(v)) clean.sessionListScroll = v; break;
      case 'plansStatusFilter': if (isPlansStatusFilter(v)) clean.plansStatusFilter = v; break;
      case 'workItemsKind': if (isWorkItemKind(v)) clean.workItemsKind = v; break;
      case 'workItemsState': if (isWorkItemState(v)) clean.workItemsState = v; break;
      case 'workItemsRepos': if (isStringArray(v)) clean.workItemsRepos = [...v]; break;
      case 'fileDrawerCollapsedProjects': if (isBoolRecord(v)) clean.fileDrawerCollapsedProjects = { ...v }; break;
      case 'fileDrawerCollapsedSections': if (isBoolRecord(v)) clean.fileDrawerCollapsedSections = { ...v }; break;
      case 'fileDrawerOpenSections': if (isBoolRecord(v)) clean.fileDrawerOpenSections = { ...v }; break;
      case 'fileDrawerScroll': if (isNonNegNumber(v)) clean.fileDrawerScroll = v; break;
      case 'fileDrawerUploadProject': if (typeof v === 'string') clean.fileDrawerUploadProject = v; break;
    }
  }
  return clean;
}

// ─── One-time legacy migration (R-A4: no parallel keys) ─────────────────────────
// Capture BEFORE the store is created: every folded ad-hoc key is read+deleted;
// its value seeds the store ONLY when no va-ui-state-v1 blob exists yet (an
// existing blob is newer truth — the legacy key is still deleted). Runs at module
// scope so the seeds land before the first render reads the store
// (restore-before-first-render). Phase 1 folded `voice-assistant-active-view`;
// Phase 2 (#1979) folds the WorkItems repos key + the four file-drawer keys.

const _legacySeeds: Partial<UiState> = (() => {
  try {
    // Read+delete helper: the legacy key is ALWAYS removed, seeded value or not.
    const take = (storage: Storage, key: string): string | null => {
      const raw = storage.getItem(key);
      if (raw === null) return null;
      storage.removeItem(key);
      return raw;
    };
    const legacyActiveView = take(localStorage, LEGACY_ACTIVE_VIEW_KEY);
    const legacyWiRepos = take(localStorage, LEGACY_WI_REPOS_KEY);
    const legacyCollapsedProjects = take(localStorage, LEGACY_COLLAPSED_PROJECTS_KEY);
    const legacyCollapsedSections = take(localStorage, LEGACY_COLLAPSED_SECTIONS_KEY);
    const legacyOpenSections = take(localStorage, LEGACY_OPEN_SECTIONS_KEY);
    let legacyDrawerScroll: string | null = null;
    try {
      legacyDrawerScroll = take(sessionStorage, LEGACY_DRAWER_SCROLL_KEY);
    } catch {
      /* sessionStorage unavailable (non-DOM env) — nothing to migrate */
    }

    // An existing blob is newer truth — delete-only, no seeds.
    if (localStorage.getItem(STORE_KEY) !== null) return {};

    const seeds: Partial<UiState> = {};
    if (legacyActiveView !== null) seeds.activeView = legacyActiveView === 'chat' ? 'chat' : 'list';
    if (legacyWiRepos !== null) {
      try {
        const parsed: unknown = JSON.parse(legacyWiRepos);
        if (isStringArray(parsed)) seeds.workItemsRepos = parsed;
      } catch { /* corrupt legacy value — dropped */ }
    }
    for (const [raw, key] of [
      [legacyCollapsedProjects, 'fileDrawerCollapsedProjects'],
      [legacyCollapsedSections, 'fileDrawerCollapsedSections'],
      [legacyOpenSections, 'fileDrawerOpenSections'],
    ] as const) {
      if (raw === null) continue;
      try {
        const parsed: unknown = JSON.parse(raw);
        if (isBoolRecord(parsed)) seeds[key] = parsed;
      } catch { /* corrupt legacy value — dropped */ }
    }
    if (legacyDrawerScroll !== null) {
      const px = parseInt(legacyDrawerScroll, 10);
      if (isNonNegNumber(px)) seeds.fileDrawerScroll = px;
    }
    return seeds;
  } catch {
    return {};
  }
})();

// ─── Store creation ─────────────────────────────────────────────────────────────

export const useUiStateStore = create<UiState>()(
  persist(
    (): UiState => ({ ...DEFAULT_UI_STATE }),
    {
      name: STORE_KEY,
      version: 1,
      storage: createJSONStorage(() => localStorage),
      // Whitelist EXACTLY the UiState keys — nothing outside the shared schema is
      // ever persisted (Class C stays ephemeral by construction).
      partialize: (state) => sanitizeUiState(state),
      // Rehydrate defense: any unknown key is dropped, any invalid value falls
      // back to its default. Runs for older versions AND (via merge below) the
      // current one.
      migrate: (persisted) => sanitizeUiState(persisted),
      // Belt-and-suspenders: merge() runs on EVERY rehydrate (migrate only runs on
      // version bumps) — sanitize here too so a hand-edited/corrupt blob at the
      // current version also can't inject unknown keys or invalid values.
      merge: (persisted, current) => ({ ...current, ...sanitizeUiState(persisted) }),
    },
  ),
);

if (Object.keys(_legacySeeds).length > 0) {
  useUiStateStore.setState(_legacySeeds);
}

// ─── Actions (one named action per intent — R-A2) ───────────────────────────────

/** view:list ↔ view:chat. The shell's ONLY way to change the top-level view. */
export function setActiveView(view: UiActiveView): void {
  useUiStateStore.setState({ activeView: view });
}

/** tab:current → tab:other. SessionListView's ONLY way to switch drawer tabs. */
export function setSessionListTab(tab: UiSessionListTab): void {
  useUiStateStore.setState({ sessionListTab: tab });
}

/**
 * fileDrawer:closed ↔ fileDrawer:open. Written by the drawerStore mirror
 * subscription below (the single funnel — every openDrawer/closeDrawer path,
 * including Back-gesture closes, flows through drawerStore). Not called from
 * components directly.
 */
export function setFileDrawerOpen(open: boolean): void {
  useUiStateStore.setState({ fileDrawerOpen: open });
}

// ─── Phase 2 actions (#1979) — one named action per intent (R-A2) ───────────────

/** sort:current → sort:other. SessionListView's ONLY way to change sort order. */
export function setSessionListSort(sort: UiSessionListSort): void {
  useUiStateStore.setState({ sessionListSort: sort });
}

/** listFilters:current → listFilters:other. Patches only the flags it is given. */
export function setSessionListFilters(patch: { showInProgress?: boolean; showAutonomous?: boolean }): void {
  const delta: Partial<UiState> = {};
  if (patch.showInProgress !== undefined) delta.sessionListShowInProgress = patch.showInProgress;
  if (patch.showAutonomous !== undefined) delta.sessionListShowAutonomous = patch.showAutonomous;
  useUiStateStore.setState(delta);
}

/** issueFilter:set ↔ issueFilter:cleared. */
export function setSessionListIssueFilter(issue: number | null): void {
  useUiStateStore.setState({ sessionListIssueFilter: issue });
}

/** listScroll:top → listScroll:scrolled. Written by the debounced scroll listener. */
export function setSessionListScroll(px: number): void {
  useUiStateStore.setState({ sessionListScroll: px });
}

/** plansFilter:current → plansFilter:other. PlansTab's ONLY way to change the filter. */
export function setPlansStatusFilter(filter: UiPlansStatusFilter): void {
  useUiStateStore.setState({ plansStatusFilter: filter });
}

/** wiFilters:current → wiFilters:other. Patches only the filters it is given. */
export function setWorkItemsFilters(patch: { kind?: UiWorkItemKind; state?: UiWorkItemState }): void {
  const delta: Partial<UiState> = {};
  if (patch.kind !== undefined) delta.workItemsKind = patch.kind;
  if (patch.state !== undefined) delta.workItemsState = patch.state;
  useUiStateStore.setState(delta);
}

/** wiRepos:current → wiRepos:other. WorkItemsPanel's ONLY way to change the repo selection. */
export function setWorkItemsRepos(repos: string[]): void {
  useUiStateStore.setState({ workItemsRepos: [...repos] });
}

/** drawerProject:expanded ↔ drawerProject:collapsed (workspace-centric mode). */
export function toggleFileDrawerCollapsedProject(name: string): void {
  const prev = useUiStateStore.getState().fileDrawerCollapsedProjects;
  useUiStateStore.setState({ fileDrawerCollapsedProjects: { ...prev, [name]: !prev[name] } });
}

/** drawerSection:expanded ↔ drawerSection:collapsed (workspace-centric mode). */
export function toggleFileDrawerCollapsedSection(key: string): void {
  const prev = useUiStateStore.getState().fileDrawerCollapsedSections;
  useUiStateStore.setState({ fileDrawerCollapsedSections: { ...prev, [key]: !prev[key] } });
}

/** drawerSections:current → drawerSections:other (multi-section mode; toggle + collapse-all). */
export function setFileDrawerOpenSections(sections: Record<string, boolean>): void {
  useUiStateStore.setState({ fileDrawerOpenSections: { ...sections } });
}

/** drawerScroll:top → drawerScroll:scrolled. Written by the debounced scroll listener. */
export function setFileDrawerScroll(px: number): void {
  useUiStateStore.setState({ fileDrawerScroll: px });
}

/** uploadProject:current → uploadProject:other. Records the last upload-target project. */
export function setFileDrawerUploadProject(project: string): void {
  useUiStateStore.setState({ fileDrawerUploadProject: project });
}

// ─── drawerStore mirror (store-to-store, single funnel) ─────────────────────────
// drawerStore stays dependency-free (its SE simulator imports it in isolation);
// the mirror lives HERE. Any drawer transition updates the persisted Class-B bit:
// files panel open → true; any close or swap to another panel → false.

useDrawerStore.subscribe((s) => {
  const open = s.phase === 'open' && s.panel === 'files';
  if (useUiStateStore.getState().fileDrawerOpen !== open) setFileDrawerOpen(open);
});

// ─── Boot restore (called once from the shell's mount anchor effect) ────────────

/**
 * Re-open the unified file drawer iff it was open when the page last unloaded.
 * The caller owns the history side-effect (pushing the `#files` modal entry so
 * the Back gesture still closes the restored drawer — view-layer concern); this
 * function owns the state transition (named drawerStore action — R-A2).
 *
 * Composes with the `#s/<id>` deep-link scheme by design: the deep link decides
 * WHICH SESSION anchors the chat underneath; the drawer restore is orthogonal
 * overlay state on top. Returns true when the drawer was restored.
 */
export function bootRestoreFileDrawer(pushHistoryEntry: () => void): boolean {
  if (!useUiStateStore.getState().fileDrawerOpen) return false;
  pushHistoryEntry();
  openDrawer('files');
  return true;
}

// ─── Hook selectors — the ONLY supported way to read state in React ─────────────

/** Generic selector hook (plan §3: components read via useUiState(selector)). */
export function useUiState<T>(selector: (s: UiState) => T): T {
  return useUiStateStore(selector);
}

/** Subscribe to the top-level view ('chat' is home). */
export function useUiActiveView(): UiActiveView {
  return useUiStateStore((s) => s.activeView);
}

/** Subscribe to the persisted session-list drawer tab. */
export function useUiSessionListTab(): UiSessionListTab {
  return useUiStateStore((s) => s.sessionListTab);
}

/** Subscribe to the persisted files-drawer-open bit. */
export function useUiFileDrawerOpen(): boolean {
  return useUiStateStore((s) => s.fileDrawerOpen);
}

// ─── Non-hook accessor (for effect bodies / boot code) ──────────────────────────

export function getUiState(): UiState {
  return sanitizeUiState(useUiStateStore.getState());
}
